import {
  Clear,
  Description,
  ImageOutlined,
  InsertDriveFileOutlined,
  Note,
  PictureAsPdf,
  RemoveCircleOutline,
  Slideshow,
  TableChart,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardHeader,
  CircularProgress,
  IconButton,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

import AutoTruncatingTypographyWithTooltip from '../../components/AutoTruncatingTypographyWithTooltip';
import { AzureIndexCitation } from '../../Types/azureIndexCitations';
import { CustomFile } from '../../Types/file';
import { LlmEnum, llms } from '../../Types/llm';

const IMAGE_MIME_TYPE_ALLOWLIST = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
];

const StyledCard = styled(Card)({
  height: '100%',
  marginRight: 'auto',
});

export type FileCardProps = {
  fileToUpload?: File;
  uploadedFile?: CustomFile;
  azureCitations?: AzureIndexCitation;
  handleFileDelete?: () => void;
  llm?: LlmEnum;
  isStarterFile?: boolean;
  onPromptForm?: boolean;
};

export const createCitationFilepath = (citation: AzureIndexCitation, index: number) => {
  let citationFilename = '';

  if (citation.filepath && citation.chunk_id) {
    if (citation.filepath.length > 50) {
      const citationLength = citation.filepath.length;
      citationFilename = `${citation.reindex_id}. ${citation.filepath.substring(
        0,
        20
      )}...${citation.filepath.substring(citationLength - 20)} - Part ${
        parseInt(citation.chunk_id) + 1
      }`;
    } else {
      citationFilename = `${citation.reindex_id}. ${citation.filepath} - Part ${
        parseInt(citation.chunk_id) + 1
      }`;
    }
  } else if (citation.filepath && citation.reindex_id) {
    citationFilename = `${citation.reindex_id}. ${citation.filepath} - Part ${citation.reindex_id}`;
  } else {
    citationFilename = `Citation ${index}`;
  }
  return citationFilename;
};

export default function FileCard({
  fileToUpload,
  uploadedFile,
  azureCitations,
  handleFileDelete,
  llm,
  isStarterFile = false,
  onPromptForm = false,
}: FileCardProps) {
  const [currentFile, setCurrentFile] = useState<CustomFile | undefined>(uploadedFile);
  const showDelete = (!onPromptForm && !isStarterFile) || !!fileToUpload;

  const [sendAsImage, setSendAsImage] = useState(
    currentFile &&
      IMAGE_MIME_TYPE_ALLOWLIST.includes(currentFile.mimetype) &&
      llms[llm as LlmEnum]?.imagesAllowed
  );

  const getIconForFileType = useCallback((mimetype: string) => {
    const iconMap: { pattern: RegExp; Icon: React.ElementType; color?: string }[] = [
      { pattern: /pdf/, Icon: PictureAsPdf, color: 'error' },
      { pattern: /word|msword|doc/, Icon: Description, color: 'primary' },
      { pattern: /excel|spreadsheet/, Icon: TableChart, color: 'success' },
      { pattern: /powerpoint|presentation/, Icon: Slideshow, color: 'warning' },
      { pattern: /onenote/, Icon: Note, color: 'secondary' },
    ];

    for (const { pattern, Icon, color } of iconMap) {
      if (pattern.test(mimetype.toLowerCase())) {
        return (
          <Icon fontSize="small" sx={{ mr: 1, width: '16px', height: '16px' }} color={color} />
        );
      }
    }

    return <InsertDriveFileOutlined fontSize="small" sx={{ mr: 1 }} />;
  }, []);

  useEffect(() => {
    if (uploadedFile) {
      setCurrentFile(uploadedFile);
    }
  }, [uploadedFile]);

  useEffect(() => {
    setSendAsImage(
      currentFile &&
        IMAGE_MIME_TYPE_ALLOWLIST.includes(currentFile.mimetype) &&
        llms[llm as LlmEnum]?.imagesAllowed
    );
  }, [currentFile, llm]);

  return (
    <StyledCard>
      <CardHeader
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: 0,
          paddingBottom: 0,
        }}
        title={
          <div style={{ display: 'flex' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {!currentFile ? (
                <InsertDriveFileOutlined
                  fontSize="small"
                  sx={{ mr: 1, width: '16px', height: '16px' }}
                />
              ) : fileToUpload ? (
                <CircularProgress
                  size={16}
                  style={{ marginRight: '10px', width: '16px', height: '16px' }}
                />
              ) : currentFile?.deleted ? (
                <Clear />
              ) : sendAsImage && (currentFile ? !currentFile.deleted : true) ? (
                <ImageOutlined
                  fontSize="small"
                  style={{ marginRight: '10px', width: '16px', height: '16px' }}
                />
              ) : (
                currentFile && <Box>{getIconForFileType(currentFile.mimetype)}</Box>
              )}

              {onPromptForm || isStarterFile ? (
                <AutoTruncatingTypographyWithTooltip
                  text={
                    (currentFile ? currentFile.fileName : '') +
                    (fileToUpload ? fileToUpload.name : '') +
                    (azureCitations
                      ? createCitationFilepath(azureCitations, Number(azureCitations.reindex_id))
                      : '')
                  }
                  variant="caption"
                  sx={{ fontSize: '12px' }}
                />
              ) : (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  <AutoTruncatingTypographyWithTooltip
                    text={
                      (currentFile ? currentFile.fileName : '') +
                      (fileToUpload ? fileToUpload.name : '') +
                      (azureCitations
                        ? createCitationFilepath(azureCitations, Number(azureCitations.reindex_id))
                        : '')
                    }
                    variant="caption"
                    clampValue={1}
                    sx={{ fontSize: '12px' }}
                  />
                  {(currentFile || fileToUpload) && (
                    <Typography sx={{ color: 'grey' }} variant="caption" fontSize="12px">
                      {currentFile ? Math.ceil((currentFile.size / (1024 * 1024)) * 10) / 10 : ''}
                      {fileToUpload ? Math.ceil((fileToUpload.size / (1024 * 1024)) * 10) / 10 : ''}
                      {'MB'}
                    </Typography>
                  )}
                </Box>
              )}
            </div>

            {showDelete && handleFileDelete && (
              <IconButton
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  e.stopPropagation();
                  handleFileDelete();
                }}
                size="small"
                sx={{ left: '10px', marginLeft: '5px', padding: '4px' }}
              >
                <Tooltip title="Remove File">
                  <RemoveCircleOutline fontSize="small" sx={{ width: '16px', height: '16px' }} />
                </Tooltip>
              </IconButton>
            )}
          </div>
        }
      ></CardHeader>
      {currentFile?.deleted && (
        <Typography
          variant="caption"
          component="p"
          fontSize="small"
          style={{ padding: '0 16px 10px' }}
        >
          Your file has been deleted
        </Typography>
      )}
    </StyledCard>
  );
}
